<template>
    <div
        class="app-toolbar"
        :style="{
            '--toolbar-font-color': toolbarFontColor,
            backgroundColor: whitelabelColor,
        }"
    >
        <transition name="fade">
            <div v-if="userInfo" class="l-inline l-center-v">
                <router-link
                    v-if="hasMapAccess"
                    to="/map"
                    class="app-toolbar__link"
                >
                    <MapSearchIcon v-if="isAxRoad" width="24" height="24" />
                    <span>{{ $t('map') }}</span>
                </router-link>

                <router-link
                    v-if="hasDashboardAccess"
                    to="/dashboard"
                    class="app-toolbar__link"
                >
                    <TruckRightIcon v-if="isAxRoad" width="24" height="24" />
                    <span>{{ $t('dashboard') }}</span>
                </router-link>

                <router-link
                    v-if="hasEcoScoreAccess"
                    to="/ecoscore"
                    class="app-toolbar__link"
                >
                    <TruckRightIcon v-if="isAxRoad" width="24" height="24" />
                    <span>{{ $t('ecoscore') }}</span>
                </router-link>

                <router-link
                    v-if="showRules"
                    to="/rules"
                    class="app-toolbar__link"
                >
                    <TruckRightIcon v-if="isAxRoad" width="24" height="24" />
                    <span>{{ $t('rules') }}</span>
                </router-link>

                <router-link
                    v-if="hasLsvaAccess"
                    to="/lsva"
                    class="app-toolbar__link"
                >
                    <TruckRightIcon v-if="isAxRoad" width="24" height="24" />
                    <span>{{ $t('lsva3') }}</span>
                </router-link>

                <div
                    class="app-toolbar__menu-icon"
                    @click="handleMenuIconClick"
                >
                    <transition name="squash-swap">
                        <RemoveIcon
                            v-if="(isAxRoad && isSidebarOpen) || showMenu"
                            width="24"
                            height="24"
                        />

                        <MenuIcon v-else width="24" height="24" />
                    </transition>
                </div>
            </div>
        </transition>

        <transition name="slide-from-bottom">
            <div
                v-if="showMenu"
                class="app-toolbar__menu"
                @click="showMenu = false"
            >
                <router-link to="/map">
                    {{ $t('map') }}
                </router-link>

                <router-link v-if="hasDashboardAccess" to="/dashboard">
                    {{ $t('dashboard') }}
                </router-link>

                <router-link v-if="hasEcoScoreAccess" to="/ecoscore">
                    {{ $t('ecoscore') }}
                </router-link>

                <router-link v-if="showRules" to="/rules">
                    {{ $t('rules') }}
                </router-link>

                <router-link v-if="hasLsvaAccess" to="/lsva">
                    {{ $t('lsva3') }}
                </router-link>

                <router-link
                    v-if="hasNavigationAccess"
                    :to="{ name: 'navigation' }"
                >
                    {{ $t('navigation') }}
                </router-link>

                <router-link
                    v-if="isSuperuser && hasMapAccess"
                    :to="{ name: 'connectionHeatmap' }"
                >
                    {{ $t('connectionHeatmap') }}
                </router-link>

                <a @click="openSettingsModal">
                    {{ $t('settings') }}
                </a>

                <a
                    v-if="$route.name !== 'changePassword'"
                    @click="openChangePasswordModal"
                >
                    {{ $t('changePassword') }}
                </a>

                <a @click="showSupportModal">
                    {{ $t('support') }}
                </a>

                <a @click="showUpdatesModal">
                    {{ $t('changelog') }}
                </a>

                <a @click="handleLogout">
                    {{ $t('logout') }}
                </a>

                <a v-if="canInstall" @click="handleInstall">
                    {{ $t('install') }}
                </a>
            </div>
        </transition>

        <div class="l-inline l-center">
            <WhitelabledLogo class="block" height="20" light />
        </div>

        <transition name="fade">
            <div v-if="userInfo">
                <transition name="fade">
                    <div
                        v-if="mapInstance && isMapViewActive && !showMenu"
                        class="app-toolbar__map-controls"
                    >
                        <MapControls
                            :disabled-controls="{ zoomOptions: true }"
                            :map="mapInstance"
                            mobile
                        />
                    </div>
                </transition>

                <a class="app-toolbar__link" @click="openDropdownPanel">
                    <PersonIcon v-if="isAxRoad" />
                    <span>
                        {{ userName }}
                    </span>
                    <ArrowDownIcon
                        :width="14"
                        :height="14"
                        class="app-toolbar__dropdown-icon"
                        :class="{
                            'app-toolbar__dropdown-icon--active': showDropdownPanel,
                        }"
                    />
                </a>

                <transition name="dropdown">
                    <div v-if="showDropdownPanel" class="app-toolbar__dropdown">
                        <router-link
                            v-if="hasNavigationAccess"
                            :to="{ name: 'navigation' }"
                        >
                            <NavigationIcon width="16" height="16" />

                            {{ $t('navigation') }}
                        </router-link>

                        <router-link
                            v-if="isSuperuser && hasMapAccess"
                            :to="{ name: 'connectionHeatmap' }"
                        >
                            <HeatmapIcon width="16" height="16" />

                            {{ $t('connectionHeatmap') }}
                        </router-link>

                        <a @click="openSettingsModal">
                            <SettingsSliderIcon width="16" height="16" />

                            {{ $t('settings') }}
                        </a>

                        <a
                            v-if="$route.name !== 'changePassword'"
                            @click="openChangePasswordModal"
                        >
                            <ChangePasswordIcon width="16" height="16" />

                            {{ $t('changePassword') }}
                        </a>

                        <a @click="showSupportModal">
                            <HeadphonesIcon width="16" height="16" />

                            {{ $t('support') }}
                        </a>

                        <a @click="showUpdatesModal">
                            <InfoIcon width="16" height="16" />

                            {{ $t('changelog') }}
                        </a>

                        <a @click="handleLogout">
                            <LogoutIcon width="16" height="16" />

                            {{ $t('logout') }}
                        </a>

                        <a v-if="canInstall" @click="handleInstall">
                            <InstallationIcon width="16" height="16" />

                            {{ $t('install') }}
                        </a>
                    </div>
                </transition>

                <VDialog />
            </div>
        </transition>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import { pwaHelper } from '@/utils'
import ArrowDownIcon from '@/components/icons/ArrowDownIcon'
import ChangePasswordIcon from '@/components/icons/ChangePasswordIcon'
import ChangePasswordModal from '@/components/ChangePasswordModal'
import HeadphonesIcon from '@/components/icons/HeadphonesIcon'
import HeatmapIcon from '@/components/icons/HeatmapIcon'
import InfoIcon from '@/components/icons/InfoIcon'
import InstallationIcon from '@/components/icons/InstallationIcon'
import LogoutIcon from '@/components/icons/LogoutIcon'
import MapControls from '@/components/redesigned/MapControls'
import MenuIcon from '@/components/icons/MenuIcon'
import NavigationIcon from '@/components/icons/NavigationIcon'
import RemoveIcon from '@/components/icons/RemoveIcon'
import SettingsModal from '@/components/redesigned/SettingsModal'
import SettingsSliderIcon from '@/components/icons/SettingsSliderIcon'
import SupportModal from '@/components/redesigned/SupportModal'
import WhitelabledLogo from '@/components/WhitelabledLogo'
import Whitelabling from '@/mixins/Whitelabling'
import MapSearchIcon from '@/components/icons/MapSearchIcon.vue'
import TruckRightIcon from '@/components/icons/TruckRightIcon.vue'
import PersonIcon from '@/components/icons/PersonIcon.vue'

export default {
    name: 'AppToolbar',
    components: {
        PersonIcon,
        TruckRightIcon,
        MapSearchIcon,
        ArrowDownIcon,
        ChangePasswordIcon,
        HeadphonesIcon,
        HeatmapIcon,
        InfoIcon,
        InstallationIcon,
        LogoutIcon,
        MapControls,
        MenuIcon,
        NavigationIcon,
        RemoveIcon,
        SettingsSliderIcon,
        WhitelabledLogo,
    },
    mixins: [Whitelabling],
    data() {
        return {
            canInstall: false,
            showDropdownPanel: false,
            showMenu: false,
        }
    },
    computed: {
        ...mapState('auth', ['userInfo']),
        ...mapState('axroad', ['isSidebarOpen']),
        ...mapState('map', ['mapInstance']),
        ...mapGetters('auth', [
            'hasAdvancedRuleAccess',
            'hasDashboardAccess',
            'hasEcoScoreAccess',
            'hasLsvaAccess',
            'hasMapAccess',
            'hasNavigationAccess',
            'hasRulesAccess',
            'isAxRoad',
            'isSuperuser',
        ]),
        isMapViewActive() {
            return this.$route.path.match(/^\/map/i)
        },
        userName() {
            const { first_name, last_name, username } = this.userInfo
            return `${first_name} ${last_name}`.trim() || username
        },
        showRules() {
            return (
                this.hasRulesAccess &&
                (!this.isAxRoad || this.hasAdvancedRuleAccess)
            )
        },
    },
    async mounted() {
        this.canInstall = await pwaHelper.canInstall()
    },
    methods: {
        ...mapActions('auth', ['logout']),
        ...mapMutations('axroad', ['toggleSidebar']),
        handleLogout() {
            this.logout()
            this.$router.push('/login', () => {
                this.$router.go()
            })
        },
        handleInstall() {
            pwaHelper.install()
        },
        openChangePasswordModal() {
            this.$modal.show(
                ChangePasswordModal,
                {},
                { width: 400, height: '50%' }
            )
        },
        openDropdownPanel() {
            if (this.showDropdownPanel) {
                return
            }

            this.showDropdownPanel = true

            const listener = () => {
                document.body.removeEventListener('click', listener)
                this.showDropdownPanel = false
            }

            setTimeout(() => {
                document.body.addEventListener('click', listener)
            })
        },
        openSettingsModal() {
            this.$modal.show(SettingsModal, {}, { height: '75%' })
        },
        showSupportModal() {
            this.$modal.show(SupportModal)
        },
        showUpdatesModal() {
            pwaHelper.showChangelog()
        },
        handleMenuIconClick() {
            if (this.isAxRoad) {
                this.toggleSidebar()
            } else {
                this.showMenu = !this.showMenu
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "changePassword": "Change password",
        "changelog": "Updates",
        "close": "Close",
        "connectionHeatmap": "Connection heatmap",
        "dashboard": "Management",
        "ecoscore": "EcoScore",
        "install": "Install",
        "logout": "Logout",
        "lsva3": "LSVA 3",
        "map": "Map",
        "navigation": "Navigation",
        "rules": "Rules",
        "settings": "Settings",
        "support": "Support"
    },
    "de": {
        "changePassword": "Passwort ändern",
        "changelog": "Updates",
        "close": "Schliessen",
        "connectionHeatmap": "Verbindungsqualität",
        "dashboard": "Verwaltung",
        "ecoscore": "EcoScore",
        "install": "Installieren",
        "logout": "Logout",
        "lsva3": "LSVA 3",
        "map": "Karte",
        "navigation": "Navigation",
        "rules": "Regeln",
        "settings": "Einstellungen",
        "support": "Support"
    },
    "fr": {
        "changePassword": "Modifier le mot de passe",
        "changelog": "Mises à jour",
        "close": "Fermer",
        "connectionHeatmap": "Qualité de la connexion",
        "dashboard": "Management",
        "ecoscore": "EcoScore",
        "install": "Installer",
        "logout": "Se déconnecter",
        "lsva3": "RPLP 3",
        "map": "Carte",
        "navigation": "Navigation",
        "rules": "Règles",
        "settings": "Paramètres",
        "support": "Assistance"
    },
    "it": {
        "changePassword": "Modificare Password",
        "changelog": "Aggiornamenti",
        "close": "Chiudere",
        "connectionHeatmap": "Qualità della connessione",
        "dashboard": "Management",
        "ecoscore": "EcoScore",
        "install": "Installare",
        "logout": "Logout",
        "lsva3": "TTPCP 3",
        "map": "Mappa",
        "navigation": "Navigazione",
        "rules": "Regole",
        "settings": "Impostazione",
        "support": "Supporto"
    }
}
</i18n>

<style lang="scss" scoped>
.app-toolbar {
    $toolbar-height: 64px;
    flex: 0 0 $toolbar-height;
    width: 100%;
    height: $toolbar-height;
    padding: 0 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $color-primary;
    user-select: none;

    & > * {
        position: relative;
        width: 50%;

        &:last-child {
            text-align: right;
        }
    }

    &__link {
        position: relative;
        display: inline-flex;
        gap: 8px;
        align-items: center;
        padding: 0 1rem;
        color: var(--toolbar-font-color, $color-primary-contrast);
        line-height: $toolbar-height;
        font-weight: 600;
        font-size: 16px;
        white-space: nowrap;
        cursor: pointer;

        & > span,
        & > svg {
            opacity: 0.7;
            transition: opacity 0.1s;
        }

        &:hover {
            & > span,
            & > svg {
                opacity: 1;
            }
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            margin-left: -6px;
            border: 6px solid transparent;
            border-bottom-color: $color-accent;
            opacity: 0;
        }

        &.router-link-active {
            & > span,
            & > svg,
            &::after {
                opacity: 1;
            }
        }
    }

    &__dropdown-icon {
        margin-left: 2px;
        transition: all 0.2s;

        &--active {
            margin-bottom: -1px;
            transform: rotateZ(180deg);
        }
    }

    &__dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        margin: 6px 8px 12px;
        padding: 8px 0;
        background-color: #fff;
        box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
        border-radius: 8px;
        font-size: 14px;
        text-align: left;
        z-index: 1000;

        a {
            display: block;
            padding: 12px 20px;
            transition: background-color 0.1s;
            cursor: pointer;

            &:hover {
                background-color: $color-gray-lighter-new;
            }

            svg {
                margin-right: 8px;
                margin-bottom: -3px;
            }
        }
    }

    &__menu-icon {
        display: none;
        color: var(--toolbar-font-color, $color-primary-contrast);
    }

    &__menu {
        position: absolute;
        top: $toolbar-height;
        left: 0;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: space-evenly;
        padding: 1rem;
        width: 100vw;
        height: calc(100vh - $toolbar-height);
        background-color: $color-gray-lighter-new;
        overflow-y: auto;
        z-index: 110;

        a {
            font-size: 32px;
            color: rgba(0, 0, 0, 0.3);
            text-align: center;
        }
    }

    &__map-controls {
        display: none;
    }

    @include respond-to('for-mobile-down') {
        $toolbar-height: 56px;
        flex-basis: $toolbar-height;
        height: $toolbar-height;

        &__link {
            display: none;
        }

        &__menu-icon {
            display: block;
        }

        &__menu {
            top: $toolbar-height;
            height: calc(100vh - $toolbar-height);

            a {
                font-size: 28px;
            }
        }

        &__map-controls {
            display: block;
        }
    }
}

.dropdown {
    &-enter-active,
    &-leave-active {
        transition: all 0.1s ease-out;
    }

    &-enter,
    &-leave-to {
        transform: translateY(-8px);
        opacity: 0;
    }
}

.fade {
    &-enter-active,
    &-leave-active {
        transition: all 0.2s;
    }

    &-enter,
    &-leave-to {
        opacity: 0;
    }
}

.slide-from-bottom {
    &-enter-active,
    &-leave-active {
        transition: all 0.2s;
    }

    &-enter,
    &-leave-to {
        opacity: 0;
        transform: translateY(1rem);
    }
}

.squash-swap {
    &-enter-active,
    &-leave-active {
        transition: all 0.2s;
    }

    &-enter,
    &-leave-to {
        position: absolute;
        transform: scaleY(0.1);
        opacity: 0;
    }
}
</style>
